import {
  Bell,
  CreditCard,
  Handshake,
  LifeBuoy,
  LucideIcon,
  Megaphone,
  MessagesSquare,
  Package,
  Server,
  Sparkles,
  UserPlus,
} from 'lucide-react';

import type { NotificationCategory, NotificationItem } from '../../common/hooks/use-notifications-api.hook';

/**
 * How a notification looks, shared by the bell and the Notifications page.
 * The words come from the API; only the glyph and the grouping are decided here.
 */

export const CATEGORY_ICONS: Record<NotificationCategory, LucideIcon> = {
  leads: UserPlus,
  ardi: Sparkles,
  catalogue: Package,
  marketing: Megaphone,
  channels: MessagesSquare,
  billing: CreditCard,
  support: LifeBuoy,
  sales: Handshake,
  platform: Server,
  other: Bell,
};

/** The order the page lists categories in — what a person acts on first. */
export const CATEGORY_ORDER: NotificationCategory[] = ['leads', 'ardi', 'catalogue', 'marketing', 'channels', 'billing', 'support', 'sales', 'platform', 'other'];

export const categoryKey = (category?: string) => `ARDI.NOTIFICATIONS.CATEGORY.${(category || 'other').toUpperCase()}`;

/**
 * Only the severities that mean something get a colour. Everything else is
 * grey: a bell of green and blue dots says nothing.
 */
export const SEVERITY_DOT: Record<string, string | null> = {
  error: 'bg-destructive',
  warning: 'bg-warning',
  success: null,
  info: null,
};

export type DayGroup = 'today' | 'yesterday' | 'week' | 'earlier';

export function dayGroup(iso: string, now = new Date()): DayGroup {
  const at = new Date(iso);
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const t = at.getTime();
  if (t >= startOfToday) return 'today';
  if (t >= startOfToday - 86_400_000) return 'yesterday';
  if (t >= startOfToday - 6 * 86_400_000) return 'week';
  return 'earlier';
}

/** Items in their day groups, in order, empty groups left out. */
export function groupByDay(items: NotificationItem[], now = new Date()): { group: DayGroup; items: NotificationItem[] }[] {
  const order: DayGroup[] = ['today', 'yesterday', 'week', 'earlier'];
  const map = new Map<DayGroup, NotificationItem[]>();
  for (const item of items) {
    const g = dayGroup(item.createdAt, now);
    map.set(g, [...(map.get(g) ?? []), item]);
  }
  return order.filter((g) => map.has(g)).map((group) => ({ group, items: map.get(group)! }));
}

/** The title to show: the API's words, never the raw type. */
export const notificationTitle = (item: NotificationItem, fallback: string) => item.title?.trim() || fallback;

/** Where the notifications page lives for this portal. */
export const notificationsPath = (isSuperAdmin: boolean, tab?: 'settings') =>
  `${isSuperAdmin ? '/notifications' : '/home/notifications'}${tab ? `?tab=${tab}` : ''}`;
